import { AlertTriangle } from 'lucide-react';
import { formatDateTime } from '../../utils';
import styles from './BookingInfoPanel.module.css';

/**
 * BookingInfoPanel
 *
 * Shared header block shown above booking forms/modals. Renders:
 *   1. A "TEE TIME" label row with the formatted tee time string.
 *   2. Status badges (Problematic / Unvalidated) when applicable.
 *   3. A warnings box (only when isProblematic is true) with Recheck
 *      and optional Approve actions.
 *
 * Props:
 *   teeTimeLabel        string         Pre-formatted tee time display string
 *   isProblematic       bool
 *   isValidated         bool
 *   warnings            string[]
 *   warningsLastChecked string | null
 *   onRecheck           function
 *   onApprove           function | null  Pass null to hide Approve button
 *   recheckLoading      bool
 *   approveLoading      bool
 *   disabled            bool            Disables both buttons when page is busy
 *   isCancelled         bool
 *   isArchived          bool
 */
export default function BookingInfoPanel({
  teeTimeLabel,
  isProblematic,
  isValidated,
  warnings = [],
  warningsLastChecked,
  onRecheck,
  onApprove,
  recheckLoading = false,
  approveLoading = false,
  disabled = false,
  isCancelled = false,
  isArchived = false,
}) {
  return (
    <>
      <div className={styles.infoSection}>
        <div className={styles.infoRow}>
          <span className={styles.infoRowLabel}>Tee time</span>
          <span>{teeTimeLabel || '—'}</span>
        </div>
        {(isCancelled || isArchived || isProblematic || !isValidated) && (
          <div className={styles.badges}>
            {isCancelled   && <span className={styles.badgeCancelled}   role="status">Cancelled</span>}
            {isArchived    && <span className={styles.badgeArchived}    role="status">Archived</span>}
            {isProblematic && <span className={styles.badgeProblematic} role="status">Problematic</span>}
            {!isValidated  && <span className={styles.badgeUnvalidated} role="status">Unvalidated</span>}
          </div>
        )}
      </div>

      {isProblematic && (
        <div className={styles.warningsSection}>
          <div className={styles.warningsSectionHeader}>
            <AlertTriangle size={13} strokeWidth={1.5} className={styles.warningIcon} />
            <span className={styles.warningsSectionTitle}>Warnings</span>
            {warningsLastChecked && (
              <span className={styles.warningsLastChecked}>
                Last checked: {formatDateTime(warningsLastChecked)}
              </span>
            )}
          </div>
          {warnings.length > 0 && (
            <ul className={styles.warningsList}>
              {warnings.map((w, i) => (
                <li key={i} className={styles.warningsListItem}>{w}</li>
              ))}
            </ul>
          )}
          <div className={styles.warningsActions}>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onRecheck}
              disabled={disabled || recheckLoading || approveLoading}
            >
              {recheckLoading ? 'Rechecking…' : 'Recheck'}
            </button>
            {onApprove && (
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={onApprove}
                disabled={disabled || recheckLoading || approveLoading}
              >
                {approveLoading ? 'Approving…' : 'Approve'}
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
